import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Search, QrCode, Calendar, User, Printer, Download, X, Plus, ExternalLink, Copy, Check } from 'lucide-react';
import { useWorkflowStore } from '../store/workflowStore';
import { toast } from '../components/Toast';
import type { WorkflowWarranty } from '../types/workflow';
import QRCode from 'qrcode';

const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('en-LK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

function getWarrantyStatus(w: WorkflowWarranty) {
  const now = Date.now();
  const productExpiry = new Date(w.productWarrantyExpiry).getTime();
  const serviceExpiry = new Date(w.serviceWarrantyExpiry).getTime();
  const isProductActive = productExpiry > now;
  const isServiceActive = serviceExpiry > now;
  return { isProductActive, isServiceActive };
}

function daysUntil(dateStr: string) {
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export default function WarrantyPage() {
  const { warranties, activateWarranty, invoices } = useWorkflowStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDetail, setShowDetail] = useState<WorkflowWarranty | null>(null);
  const [showQR, setShowQR] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const filtered = warranties.filter(w => {
    const matchSearch =
      w.customerName.toLowerCase().includes(search.toLowerCase()) ||
      w.warrantyId.toLowerCase().includes(search.toLowerCase()) ||
      (w.invoiceNumber || '').toLowerCase().includes(search.toLowerCase());
    const { isProductActive } = getWarrantyStatus(w);
    const status = isProductActive ? 'active' : 'expired';
    const matchStatus = statusFilter === 'all' || status === statusFilter;
    return matchSearch && matchStatus;
  });

  // Generate QR code when opening QR modal
  useEffect(() => {
    if (showQR && showDetail) {
      const url = `${window.location.origin}/warranty/verify/${showDetail.warrantyId}`;
      QRCode.toDataURL(url, { width: 280, margin: 2, color: { dark: '#1a1a1a', light: '#ffffff' } })
        .then(dataUrl => setQrDataUrl(dataUrl))
        .catch(err => { console.error(err); setQrDataUrl(''); });
    }
  }, [showQR, showDetail]);

  const handleCopyLink = () => {
    if (!showDetail) return;
    const url = `${window.location.origin}/warranty/verify/${showDetail.warrantyId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copied!', 'Warranty verification link copied to clipboard.');
    });
  };

  const handleDownloadQR = () => {
    if (!qrDataUrl || !showDetail) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `warranty-qr-${showDetail.warrantyId}.png`;
    a.click();
    toast.success('Downloaded', 'QR code downloaded as PNG.');
  };

  const handlePrint = (warranty: WorkflowWarranty) => {
    const { isProductActive, isServiceActive } = getWarrantyStatus(warranty);
    const printWindow = window.open('', '_blank', 'width=800,height=900');
    if (!printWindow) return;

    const productDaysLeft = daysUntil(warranty.productWarrantyExpiry);
    const serviceDaysLeft = daysUntil(warranty.serviceWarrantyExpiry);

    printWindow.document.write(`
<!DOCTYPE html>
<html>
<head>
  <title>K-Tec Warranty Certificate — ${warranty.warrantyId}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 40px; color: #1a1a1a; background: #fff; }
    .header { text-align: center; margin-bottom: 32px; }
    .logo { font-size: 28px; font-weight: 800; color: #7FA86A; letter-spacing: -0.5px; }
    .subtitle { font-size: 13px; color: #888; margin-top: 4px; }
    .cert-title { font-size: 22px; font-weight: 700; margin: 24px 0 8px; }
    .warranty-id { font-family: monospace; font-size: 15px; color: #7FA86A; font-weight: 700; }
    .section { background: #f8f8f8; border-radius: 12px; padding: 20px; margin-bottom: 16px; }
    .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #888; margin-bottom: 12px; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .field label { font-size: 11px; color: #888; display: block; }
    .field p { font-size: 14px; font-weight: 600; margin: 2px 0 0; }
    .status { display: inline-block; padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 700; }
    .status.active { background: #dcfce7; color: #16a34a; }
    .status.expired { background: #fee2e2; color: #dc2626; }
    .product-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; font-size: 13px; }
    .footer { margin-top: 32px; text-align: center; font-size: 11px; color: #aaa; }
    .divider { border: none; border-top: 1px solid #e5e5e5; margin: 20px 0; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo">K-Tec Solutions</div>
    <div class="subtitle">Warranty & Service Certificate</div>
  </div>
  <hr class="divider" />
  <div class="cert-title">Warranty Certificate</div>
  <div class="warranty-id">${warranty.warrantyId}</div>
  <div class="section" style="margin-top:16px">
    <div class="section-title">Customer Information</div>
    <div class="grid">
      <div class="field"><label>Name</label><p>${warranty.customerName}</p></div>
      <div class="field"><label>Phone</label><p>${warranty.customerPhone || '—'}</p></div>
      <div class="field"><label>Invoice Reference</label><p>${warranty.invoiceNumber || '—'}</p></div>
      <div class="field"><label>Installation Date</label><p>${fmtDate(warranty.installationDate)}</p></div>
    </div>
  </div>
  <div class="section">
    <div class="section-title">Product Warranty</div>
    <div class="grid">
      <div class="field">
        <label>Status</label>
        <p><span class="status ${isProductActive ? 'active' : 'expired'}">${isProductActive ? 'ACTIVE' : 'EXPIRED'}</span></p>
      </div>
      <div class="field"><label>Duration</label><p>${warranty.productWarrantyDuration}</p></div>
      <div class="field"><label>Start Date</label><p>${fmtDate(warranty.productWarrantyStart)}</p></div>
      <div class="field"><label>Expiry Date</label><p>${fmtDate(warranty.productWarrantyExpiry)}</p></div>
    </div>
    ${isProductActive && productDaysLeft <= 90 ? `<p style="color:#f59e0b;font-size:12px;margin-top:8px">⚠️ Expires in ${productDaysLeft} days</p>` : ''}
  </div>
  <div class="section">
    <div class="section-title">Service Warranty</div>
    <div class="grid">
      <div class="field">
        <label>Status</label>
        <p><span class="status ${isServiceActive ? 'active' : 'expired'}">${isServiceActive ? 'ACTIVE' : 'EXPIRED'}</span></p>
      </div>
      <div class="field"><label>Duration</label><p>${warranty.serviceWarrantyDuration}</p></div>
      <div class="field"><label>Start Date</label><p>${fmtDate(warranty.serviceWarrantyStart)}</p></div>
      <div class="field"><label>Expiry Date</label><p>${fmtDate(warranty.serviceWarrantyExpiry)}</p></div>
    </div>
  </div>
  <div class="section">
    <div class="section-title">Installed Equipment</div>
    ${warranty.items.map(item => `
      <div class="product-row">
        <span>${item.productName}${item.modelNumber ? ` (${item.modelNumber})` : ''} × ${item.quantity}</span>
        <span>${item.warrantyPeriod}</span>
      </div>
    `).join('')}
  </div>
  <div class="section" style="text-align:center">
    <p style="font-size:12px;color:#888;margin-bottom:8px">Scan to verify this warranty online:</p>
    <p style="font-size:13px;color:#7FA86A;font-weight:600">${window.location.origin}/warranty/verify/${warranty.warrantyId}</p>
  </div>
  <div class="footer">
    <p>K-Tec Solutions · Warranty Certificate · Generated ${new Date().toLocaleDateString('en-LK')}</p>
    <p>This certificate is valid only when verified at the URL above.</p>
  </div>
</body>
</html>`);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const handleDownloadPDF = (warranty: WorkflowWarranty) => {
    // Open print view optimized for PDF
    handlePrint(warranty);
    toast.info('PDF Download', 'Use your browser\'s "Save as PDF" option in the print dialog.');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">Warranty Management</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">{warranties.length} total warranty records</p>
        </div>
        <button
          onClick={() => toast.info('Activate Warranty', 'Warranties are created automatically when an invoice is marked as Paid or Complete.')}
          className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />How to Create
        </button>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by customer, warranty ID or invoice…"
            className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" />
        </div>
        <div className="flex bg-white border border-gray-200/60 rounded-xl overflow-hidden">
          {['all', 'active', 'expired'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 h-10 text-[12px] font-medium capitalize transition-all ${statusFilter === s ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.length === 0 && (
          <div className="col-span-full bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-12 text-center">
            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-[15px] font-semibold text-gray-500">
              {search || statusFilter !== 'all' ? 'No warranties match your filter' : 'No warranty records yet'}
            </p>
            <p className="text-[12px] text-gray-400 mt-1">
              Warranties are created automatically when an invoice payment is completed or when you click "Mark Complete" on an invoice.
            </p>
          </div>
        )}
        {filtered.map((w, i) => {
          const { isProductActive } = getWarrantyStatus(w);
          const daysLeft = daysUntil(w.productWarrantyExpiry);
          return (
            <motion.div key={w.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-5 hover:shadow-lg hover:shadow-gray-200/40 transition-all cursor-pointer"
              onClick={() => setShowDetail(w)}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isProductActive ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    <Shield className={`w-5 h-5 ${isProductActive ? 'text-emerald-500' : 'text-red-500'}`} />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-semibold text-gray-900">{w.customerName}</h3>
                    <p className="text-[11px] text-gray-400 font-mono">{w.warrantyId}</p>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${isProductActive ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                  {isProductActive ? 'ACTIVE' : 'EXPIRED'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                <div><span className="text-gray-400">Installed</span><p className="text-gray-700 font-medium">{fmtDate(w.installationDate)}</p></div>
                <div><span className="text-gray-400">Expires</span><p className={`font-medium ${isProductActive && daysLeft <= 90 ? 'text-amber-600' : 'text-gray-700'}`}>{fmtDate(w.productWarrantyExpiry)}</p></div>
                <div><span className="text-gray-400">Invoice</span><p className="text-gray-700">{w.invoiceNumber || '—'}</p></div>
                <div><span className="text-gray-400">Products</span><p className="text-gray-700">{w.items.length} item(s)</p></div>
              </div>
              {isProductActive && daysLeft <= 90 && (
                <div className="mt-3 p-2 bg-amber-50 rounded-xl text-[11px] text-amber-600 font-medium">
                  ⚠️ Expiring in {daysLeft} days
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* WARRANTY DETAIL MODAL */}
      <AnimatePresence>
        {showDetail && !showQR && (
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDetail(null)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-gray-200/60 p-6 overflow-y-auto max-h-[90vh]" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-[18px] font-semibold text-gray-900">Warranty Certificate</h2>
                <button onClick={() => setShowDetail(null)} className="p-1.5 rounded-xl hover:bg-gray-100"><X className="w-5 h-5 text-gray-400" /></button>
              </div>

              {/* Certificate Card */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 text-white mb-5">
                <div className="flex items-center gap-2 mb-1">
                  <Shield className="w-5 h-5 text-[#7FA86A]" />
                  <span className="text-[14px] font-bold text-[#7FA86A]">K-Tec Solutions</span>
                </div>
                <p className="text-[11px] text-gray-400 mb-4">Warranty Certificate</p>
                <div className="grid grid-cols-2 gap-4 text-[13px]">
                  <div><span className="text-gray-400 text-[11px]">Warranty ID</span><p className="font-mono font-bold text-[#7FA86A]">{showDetail.warrantyId}</p></div>
                  <div><span className="text-gray-400 text-[11px]">Customer</span><p className="font-semibold">{showDetail.customerName}</p></div>
                  <div><span className="text-gray-400 text-[11px]">Phone</span><p>{showDetail.customerPhone || '—'}</p></div>
                  <div><span className="text-gray-400 text-[11px]">Invoice</span><p>{showDetail.invoiceNumber || '—'}</p></div>
                  <div><span className="text-gray-400 text-[11px]">Installation</span><p>{fmtDate(showDetail.installationDate)}</p></div>
                  <div><span className="text-gray-400 text-[11px]">Products</span><p>{showDetail.items.length} item(s)</p></div>
                </div>

                {/* Warranty Status */}
                {(() => {
                  const { isProductActive, isServiceActive } = getWarrantyStatus(showDetail);
                  return (
                    <div className="mt-4 pt-4 border-t border-gray-700 grid grid-cols-2 gap-3 text-[12px]">
                      <div>
                        <span className="text-gray-400">Product Warranty</span>
                        <div className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${isProductActive ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-400'}`}>
                          {isProductActive ? '✓ ACTIVE' : '✗ EXPIRED'}
                        </div>
                        <p className="text-gray-400 mt-1">{showDetail.productWarrantyDuration} · Exp: {fmtDate(showDetail.productWarrantyExpiry)}</p>
                      </div>
                      <div>
                        <span className="text-gray-400">Service Warranty</span>
                        <div className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${isServiceActive ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-400'}`}>
                          {isServiceActive ? '✓ ACTIVE' : '✗ EXPIRED'}
                        </div>
                        <p className="text-gray-400 mt-1">{showDetail.serviceWarrantyDuration} · Exp: {fmtDate(showDetail.serviceWarrantyExpiry)}</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Products */}
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <span className="text-[11px] text-gray-400">Installed Equipment</span>
                  {showDetail.items.map(p => (
                    <div key={p.id} className="flex justify-between items-center text-[12px] mt-2">
                      <span className="font-medium">{p.productName}{p.modelNumber ? ` — ${p.modelNumber}` : ''} × {p.quantity}</span>
                      <span className="text-gray-400">{p.warrantyPeriod}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowQR(true)}
                  className="h-10 px-4 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200 flex items-center gap-2"
                >
                  <QrCode className="w-4 h-4" />QR Code
                </button>
                <button
                  onClick={() => handlePrint(showDetail)}
                  className="h-10 px-4 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200 flex items-center gap-2"
                >
                  <Printer className="w-4 h-4" />Print
                </button>
                <button
                  onClick={() => handleDownloadPDF(showDetail)}
                  className="h-10 px-4 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />Download PDF
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* QR CODE MODAL */}
        {showDetail && showQR && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowQR(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-sm bg-white rounded-3xl shadow-2xl border border-gray-200/60 p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-[17px] font-semibold text-gray-900">Warranty QR Code</h2>
                <button onClick={() => setShowQR(false)} className="p-1.5 rounded-xl hover:bg-gray-100"><X className="w-5 h-5 text-gray-400" /></button>
              </div>

              <div className="text-center">
                <p className="text-[12px] font-mono text-[#7FA86A] font-bold mb-4">{showDetail.warrantyId}</p>
                {qrDataUrl ? (
                  <div className="inline-block p-3 bg-white border-2 border-gray-200 rounded-2xl">
                    <img src={qrDataUrl} alt="Warranty QR Code" className="w-56 h-56" />
                  </div>
                ) : (
                  <div className="w-56 h-56 mx-auto bg-gray-100 rounded-2xl flex items-center justify-center">
                    <span className="text-gray-400 text-sm">Generating…</span>
                  </div>
                )}
                <p className="text-[11px] text-gray-400 mt-3">Scan to verify warranty online</p>
                <div className="mt-3 px-3 py-2 bg-gray-50 rounded-xl text-[11px] text-gray-500 font-mono break-all">
                  {window.location.origin}/warranty/verify/{showDetail.warrantyId}
                </div>
              </div>

              <div className="flex gap-2 mt-5">
                <button
                  onClick={handleCopyLink}
                  className="flex-1 h-10 rounded-xl bg-gray-100 text-gray-700 text-[13px] font-medium hover:bg-gray-200 flex items-center justify-center gap-2"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
                <button
                  onClick={handleDownloadQR}
                  disabled={!qrDataUrl}
                  className="flex-1 h-10 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />Download QR
                </button>
              </div>

              <button
                onClick={() => window.open(`/warranty/verify/${showDetail.warrantyId}`, '_blank')}
                className="w-full mt-2 h-9 rounded-xl bg-[#F1F6EE] text-[#7FA86A] text-[12px] font-medium hover:bg-[#E8F2E3] flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-3.5 h-3.5" />Open Verification Page
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
